/** An amount in one currency, as the backend reports a figure split by event currency. */
export type CurrencyAmount = {currency: string; amount: number};

/**
 * A total that spans events selling in different currencies, one figure per currency, e.g.
 * "N$300.00 · €59.00". Summing them would need an exchange rate the backend deliberately does not
 * pick, so they are listed instead. Falls back to `fallbackNad` when no split was sent (an older
 * backend) or nothing was sold.
 */
export function formatMoneyByCurrency(split: CurrencyAmount[] | null | undefined, fallbackNad: number): string {
    const parts = (split ?? []).filter((r) => r.amount !== 0 || (split ?? []).length === 1);
    if (parts.length === 0) return formatMoney(fallbackNad, false);
    return parts.map((r) => formatMoney(r.amount, false, r.currency)).join(" · ");
}

export function formatMoney(amount: number, addFreeLabel = true, currency: string = "NAD", locale = "en-NA"): string {

    if (amount === 0 && addFreeLabel) {
        return "Free (RSVP)";
    }

    const isoCurrency = normalizeCurrency(currency);

    const formatted = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: isoCurrency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 2,
    }).format(amount);

    if (isoCurrency === "NAD" && formatted.startsWith("$")) {
        return "N" + formatted;
    }
    return formatted;
}

function normalizeCurrency(cur?: string): string {
    if (!cur) return "NAD";
    const c = cur.toUpperCase().trim();
    if (c === "N$" || c === "NAD$" || c === "NAD") return "NAD";
    if (c === "EUR" || c === "€") return "EUR";
    return c;
}
